<template>
  <form @submit.prevent="next">
    <h2 class="text-primary mb-5">{{ $t(`global.checkout-details`) }}</h2>
    <FormMessage :errors="errors" v-if="errors" />

    <div class="my-4">
      <h3 class="text-primary">{{ `${$t("global.checkout-location")} *` }}</h3>
      <FormSelect
        name="location"
        v-model="planDetailFields.locationId"
        test-id="location-input"
        :disabled="loading"
        :errors="validation.fields.locationId.errors"
        :dirty="validation.fields.locationId.dirty"
      >
        <option v-if="!planDetailFields.locationId" selected="true" value="">
          - {{ $t("global.select-location") }} -
        </option>
        <option
          v-for="location in locations"
          :value="location.id"
          :key="location.id"
          :selected="locationIsSelected(location.id)"
        >
          {{ location.name }}
        </option>
      </FormSelect>
    </div>

    <div class="my-4">
      <h3 class="text-primary">{{ `${$t("global.checkout-nickname")} *` }}</h3>
      <div class="text-secondary mb-3">{{ $t("global.nickname-description") }}</div>
      <FormInput
        name="nickname"
        v-model="planDetailFields.nickname"
        test-id="nickname-input"
        :errors="validation.fields.nickname.errors"
        :dirty="validation.fields.nickname.dirty"
      />
    </div>

    <!-- display the auot install checkbox; is checked by default -->
    <template v-if="showAutoInstallCheckbox">
      <div class="d-flex">
        <FormCheckbox class="mr-2" v-model="planDetailFields.installApp" />
        <h3 class="text-primary">
          {{ $t("global.auto-install-app", { name: selectedApp.name }) }}
        </h3>
      </div>
      <!-- if the selected package is magento,
        a notice about its required elasticsearch addon will be displayed here -->
      <div v-if="requiresElasticSearch" class="text-secondary">
        {{ $t("global.magento-requires-elasticsearch") }}
      </div>
    </template>

    <!-- the bigcommerce credential form;
      only shows if a bigcommerce package has been selected -->
    <template v-if="hasBigCommerce">
      <h2 class="text-primary my-3">{{ $t("global.bigcommerce-plan-details") }}</h2>
      <div class="my-4">
        <FormInput
          name="bigcommerce-api-url"
          v-model="planDetailFields.bigCommerceApiUrl"
          test-id="igcommerce-api-url-input"
          :label="`${$t('global.bigcommerce-api-url')} *`"
          :errors="validation.fields.bigCommerceApiUrl.errors"
          :dirty="validation.fields.bigCommerceApiUrl.dirty"
        />
      </div>
      <div class="my-4">
        <FormInput
          name="bigcommerce-client-id"
          v-model="planDetailFields.bigCommerceClientId"
          test-id="igcommerce-client-id-input"
          :label="`${$t('global.bigcommerce-client-id')} *`"
          :errors="validation.fields.bigCommerceClientId.errors"
          :dirty="validation.fields.bigCommerceClientId.dirty"
        />
      </div>
      <div class="my-4">
        <FormInput
          name="bigcommerce-client-secret"
          v-model="planDetailFields.bigCommerceClientSecret"
          test-id="igcommerce-client-secret-input"
          :label="`${$t('global.bigcommerce-client-secret')} *`"
          :errors="validation.fields.bigCommerceClientSecret.errors"
          :dirty="validation.fields.bigCommerceClientSecret.dirty"
        />
      </div>
      <div class="my-4">
        <FormInput
          name="bigcommerce-access-token"
          v-model="planDetailFields.bigCommerceAccessToken"
          test-id="igcommerce-access-token-input"
          :label="`${$t('global.bigcommerce-access-token')} *`"
          :errors="validation.fields.bigCommerceAccessToken.errors"
          :dirty="validation.fields.bigCommerceAccessToken.dirty"
        />
      </div>
    </template>

    <div class="text-right my-4">{{ `* ${$t("global.required")}` }}</div>
    <div class="mt-4 text-right">
      <Btn class="mr-2" variant="light" :disabled="loading" @click="back">
        {{ $t("global.back") }}
      </Btn>
      <Btn variant="primary" type="submit" :loading="loading">
        {{ $t("global.next") }}
      </Btn>
    </div>
  </form>
</template>

<script>
import { computed, onMounted, reactive, ref } from "@vue/composition-api";
import Btn from "@/components/display/Btn";
import FormCheckbox from "@/components/display/form/FormCheckbox";
import FormInput from "@/components/display/form/FormInput";
import FormSelect from "@/components/display/form/FormSelect";
import { checkoutPlanDetails as planDetailsValidationRules } from "leatherman-js/utils/validations/forms";
import store from "@/store";
import useAnalytics from "@/utils/useAnalytics";
import useValidator from "@/utils/useValidator";

export default {
  name: "CheckoutDetailsPage",
  components: {
    Btn,
    FormCheckbox,
    FormInput,
    FormSelect,
  },
  setup(props, context) {
    const loading = ref(true);
    const errors = ref(null);

    const requireStoreBuilder = computed(() => store.getters["checkout/requireStoreBuilder"]);
    const requireStoreBuilderUi = computed(() => store.getters["checkout/requireStoreBuilderUi"]);

    // currently selected package
    const selectedPackage = computed(() => {
      const { appId, packageId } = store.state.checkout;
      return store.getters["appPackages/getAppPackage"](packageId, appId);
    });

    // if a user has bigcommerce credentials they would like to use for their new plan
    // should trigger the bigcommerce form to be shown/hidden
    const hasBigCommerce = computed(() => store.state.checkout.hasBigCommerce);

    // login variables and validation
    const planDetailFields = reactive({
      locationId: store.state.checkout.locationId,
      nickname: store.state.checkout.nickname,
      installApp: store.state.checkout.installApp,
      hasBigCommerce: hasBigCommerce.value,
      bigCommerceApiUrl: null,
      bigCommerceClientId: null,
      bigCommerceClientSecret: null,
      bigCommerceAccessToken: null,
    });

    const { validator } = useValidator();
    const validation = validator(
      planDetailFields,
      planDetailsValidationRules,
      {},
      {
        location: {
          required: context.root.$t("validation.location-required"),
        },
        nickname: {
          required: context.root.$t("validation.nickname-required"),
        },
      }
    );

    const selectedApp = computed(() =>
      store.getters["apps/getAppById"](store.state.checkout.appId)
    );

    // is the selected package magento?
    // this will conditionally show the Magento 2.4 Elasticsearch requirement notice under the auto install checkbox
    const requiresElasticSearch = computed(() => selectedApp.value?.type === "magento");

    // list of available locations (first is selected by default)
    const locations = computed(() => store.getters["locations/getListByApp"](selectedApp.value));

    // visually controls which location is shown in the dropdown
    const locationIsSelected = (locationId) => String(locationId) === planDetailFields.locationId;

    // currently selected location
    const selectedLocation = computed(() =>
      store.getters["locations/getLocationById"](planDetailFields.locationId)
    );

    // is the selected package a legacy package?
    // this will conditionally show the auto-install checkbox for packages that need it
    const showAutoInstallCheckbox = computed(() => {
      return (
        selectedPackage.value.brand === "nexcess" &&
        selectedApp.value.type !== "generic" &&
        selectedLocation.value?.type !== "aws"
      );
    });

    const back = () => {
      context.root.$router.push({
        name: "checkout-package",
        query: store.getters["checkout/getQueryParams"],
      });
    };

    const { cartData } = useAnalytics();

    const next = () => {
      loading.value = true;
      errors.value = null;
      validation.reset();

      validation
        .validate()
        .then(() => {
          // set all plan details in store
          store.dispatch("checkout/setLocation", planDetailFields.locationId);
          store.dispatch("checkout/setNickname", planDetailFields.nickname);

          if (showAutoInstallCheckbox.value) {
            // if auto install checkbox is visible, use the value the user selected, otherwise set to true
            store.dispatch("checkout/setInstallApp", planDetailFields.installApp);
          } else {
            // if app type is "generic", set auto install to false
            store.dispatch("checkout/setInstallApp", selectedApp.value.type !== "generic");
          }

          store.dispatch("analytics/send", { name: "checkoutPlanDetails", data: cartData.value });

          // go to next checkout step
          if (requireStoreBuilder.value) {
            context.root.$router.push({
              name: "checkout-storebuilder",
              query: store.getters["checkout/getQueryParams"],
            });
          } else {
            context.root.$router.push({
              name: "checkout-billing",
              query: store.getters["checkout/getQueryParams"],
            });
          }
        })
        .finally(() => (loading.value = false));
    };

    onMounted(() => {
      if (
        !store.state.checkout.appId ||
        !store.state.checkout.packageId ||
        !store.getters["auth/isLoggedIn"]
      ) {
        back();
      } else {
        store.dispatch("checkout/autoRedirect", false);
        store.dispatch("locations/getList").finally(() => {
          loading.value = false;

          if (!planDetailFields.locationId) {
            // auto select the first location in the list of locations (if none is provided in the url)
            planDetailFields.locationId = store.getters["locations/getDefault"]?.id;

            store.dispatch("checkout/setLocation", planDetailFields.locationId);
          }
        });

        // redirect to store builder page immediately if ui override is set for the selected package
        if (requireStoreBuilderUi.value) {
          context.root.$router.push({
            name: "checkout-storebuilder",
            query: store.getters["checkout/getQueryParams"],
          });
        }
      }
    });

    return {
      next,
      back,
      planDetailFields,
      locations,
      validation,
      selectedPackage,
      selectedApp,
      hasBigCommerce,
      showAutoInstallCheckbox,
      requiresElasticSearch,
      locationIsSelected,
      errors,
      loading,
      requireStoreBuilder,
    };
  },
};
</script>
